import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { getRecognitionRibbonColor } from '../../../lib/recognitionLevelColor';
import { S3Image, getAvatarStyle } from '../../../lib/s3';
import { openSuccessionDrawer } from '../../../actions/successionDrawerActionCreators';
import { useSuccessionTheme } from '../SuccessionThemeProvider';
import gql from 'graphql-tag';
import client from '../../../lib/apolloClient';
import { withSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { currencySymbol } from '../../../utils/formatters';
import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client';
import { useIntl } from 'react-intl';

const formatCurrency = (value) => {
  // Handle null, undefined, empty strings, or zero values
  if (value === null || value === undefined || value === '' || value === 0) {
    return 'N/A';
  }
  
  // Parse the value to a number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Check if it's a valid number after parsing
  if (isNaN(numValue)) {
    return 'N/A';
  }
  
  // Format based on magnitude
  const sym = currencySymbol();
  if (numValue >= 1000000) {
    return `${sym}${(numValue / 1000000).toFixed(1)}M`;
  } else if (numValue >= 1000) {
    return `${sym}${(numValue / 1000).toFixed(0)}K`;
  } else {
    return `${sym}${numValue.toFixed(0)}`;
  }
};

// Helper to check if a value is a valid number (not null, not undefined, not NaN)
const isValidNumber = (value) => {
  if (value === null || value === undefined || value === '') return false;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return !isNaN(num) && num !== 0;
};

const ADD_TO_ROLODEX_MUTATION = gql`
  mutation AddToRolodex($userName: String!) {
    addNote(
        userName: $userName
        text: "Added to Rolodex"
    ) {
      id
    }
  }
`;

const REMOVE_FROM_ROLODEX_MUTATION = gql`
  mutation RemoveFromRolodex($userName: String!) {
    deleteAllNotes(userName: $userName)
  }
`;

const GOAL_KEY_MAP = {
  'Buying': 'buying',
  'Selling': 'selling',
  'Selling my practice': 'selling_my_practice',
  'Selling part of my practice': 'selling_part_of_my_practice',
  'Find a Successor': 'find_a_successor',
  'Become a Successor': 'become_a_successor',
  'Merging': 'merging',
  'Partnering': 'partnering',
  'Forming a Partnership': 'forming_a_partnership',
  'Finding a Junior Partner': 'finding_a_junior_partner',
  'Becoming a Junior Partner': 'becoming_a_junior_partner',
  'Business Continuity': 'business_continuity',
  'Mentoring': 'mentoring',
  'Being Mentored': 'being_mentored',
  'Looking for Successor': 'looking_for_successor',
  'Currently not interested in Buying / Selling': 'currentlynotinterestedin_buying/selling',
};

const ListCard = ({ user, currentCompany, enqueueSnackbar, onCardClick, avatarBadge, footer }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();

  const translateGoal = (goal) => {
    const key = GOAL_KEY_MAP[goal];
    if (!key) return goal;
    return intl.formatMessage({ id: `transition_goals.${key}`, defaultMessage: goal });
  };
  const [addingToRolodex, setAddingToRolodex] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [canMessage, setCanMessage] = useState(false);

  // Simple boolean state for rolodex - initialized from server data
  const [isUserInRolodex, setIsUserInRolodex] = useState(user.note_count > 0);

  // Update state when user changes (to handle component reuse)
  useEffect(() => {
    setIsUserInRolodex(user.note_count > 0);
  }, [user.id, user.note_count]);

  // Create a fallback Apollo client if the imported one fails
  const createFallbackClient = () => {
    return new ApolloClient({
      link: new HttpLink({
        uri: '/graphql',
        credentials: 'same-origin',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
        },
      }),
      cache: new InMemoryCache(),
    });
  };

  // Fetch current user info to determine if messaging is allowed
  useEffect(() => {
    let isMounted = true;
    
    const getCurrentUser = async () => {
      try {
        // Use imported client or fallback
        const apolloClient = client || createFallbackClient();
        
        const result = await apolloClient.query({
          query: gql`
            query GetCurrentUser {
              getCurrentUser {
                id
                user_name
                is_pure_buyer
                limited_access
                motivation
              }
            }
          `,
          fetchPolicy: 'network-only',
        });
        
        if (isMounted && result.data && result.data.getCurrentUser) {
          setCurrentUser(result.data.getCurrentUser);
          
          // Check if the user is in the visible_profile_features_for_user list
          const hasMessageFeature = user.visible_profile_features_for_user?.includes('message');
          
          // Only enable messaging if both business rules allow AND 'message' is in visible features
          const messagingAllowed = hasMessageFeature;

          setCanMessage(messagingAllowed);
        }
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };
    
    getCurrentUser();
    
    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [user, currentCompany]);

  // Set ribbon color based on motivation type
  const getRibbonColor = () => {
    if (user.is_student) return 'bg-succession-warning';
    if (user.amp_trainee_status) return 'bg-succession-secondary';
    if (user.has_partial_sales) return 'bg-succession-success';
    return '';
  };

  // Set ribbon text based on user properties
  const getRibbonText = () => {
    if (user.is_student) return intl.formatMessage({ id: 'succession.search.list_card.student' });
    if (user.amp_trainee_status) return intl.formatMessage({ id: 'succession.search.list_card.amp_trainee' });
    if (user.has_partial_sales) return intl.formatMessage({ id: 'succession.search.list_card.partial_sale' });

    // Don't show ribbon for buyers or sellers
    return '';
  };

  // Handle click to view user profile
  const handleViewProfile = () => {
    dispatch(openSuccessionDrawer(user.fb_user_name || user.user_name));
  };

  // Handle click to send message to user
  const handleSendMessage = () => {
    // First check if messaging is allowed
    if (!canMessage) {
      // Show notification that messaging is not available
      if (enqueueSnackbar) {
        enqueueSnackbar(intl.formatMessage({ id: 'succession.search.list_card.messaging_not_available' }), {
          variant: 'warning',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          }
        });
      }
      return;
    }
    
    // Set sessionStorage to open the messages tab (tab index 4)
    sessionStorage.setItem('profileDrawer_tab', '4');
    
    // Open the drawer
    dispatch(openSuccessionDrawer(user.fb_user_name || user.user_name));
  };

  // Toggle user in/out of rolodex
  const toggleRolodex = async () => {
    if (addingToRolodex) return;
    
    const newState = !isUserInRolodex;
    setIsUserInRolodex(newState); // Immediate visual feedback
    setAddingToRolodex(true);
    
    try {
      // Use imported client or fallback
      const apolloClient = client || createFallbackClient();
      
      if (newState) {
        // Add to rolodex
        const result = await apolloClient.mutate({
          mutation: ADD_TO_ROLODEX_MUTATION,
          variables: {
            userName: user.fb_user_name || user.user_name
          }
        });
        
        if (result.data && result.data.addNote) {
          // Success - state already updated above
          
          // Show success notification
          if (enqueueSnackbar) {
            enqueueSnackbar(intl.formatMessage({ id: 'succession.search.list_card.added_to_rolodex' }), {
              variant: 'success',
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
              }
            });
          }
        } else {
          // Revert on failure - something went wrong
          console.log('Add to rolodex failed:', result);
          setIsUserInRolodex(!newState);
        }
      } else {
        // Remove from rolodex
        const result = await apolloClient.mutate({
          mutation: REMOVE_FROM_ROLODEX_MUTATION,
          variables: {
            userName: user.fb_user_name || user.user_name
          }
        });
        
        if (result.data && result.data.deleteAllNotes !== undefined) {
          // Success - state already updated above
          
          // Show success notification
          if (enqueueSnackbar) {
            enqueueSnackbar(intl.formatMessage({ id: 'succession.search.list_card.removed_from_rolodex' }), {
              variant: 'success',
              anchorOrigin: {
                vertical: 'top',
                horizontal: 'center',
              }
            });
          }
        } else {
          // Revert on failure
          setIsUserInRolodex(!newState);
        }
      }
    } catch (error) {
      console.error("Error toggling rolodex:", error);
      
      // Revert the visual state on error
      setIsUserInRolodex(!newState);
      
      // Show error notification
      if (enqueueSnackbar) {
        enqueueSnackbar(newState ? intl.formatMessage({ id: 'succession.search.list_card.failed_add_rolodex' }) : intl.formatMessage({ id: 'succession.search.list_card.failed_remove_rolodex' }), {
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          }
        });
      }
    } finally {
      setAddingToRolodex(false);
    }
  };

  // Generate placeholder initials for avatar if no profile picture
  const getInitials = () => {
    if (!user.user_name) return '?';
    return user.user_name.charAt(0).toUpperCase();
  };

  const ribbonText = getRibbonText();
  const showRibbon = !!ribbonText;

  const handleCardClick = (event, handler) => {
    if (!handler) return;
    event.stopPropagation();
    handler();
  };

  return (
    <div
      className={`relative bg-succession-bg-primary rounded-lg shadow-succession-md overflow-visible transition-all duration-300 min-h-[200px] h-auto border border-succession-border ${
        onCardClick ? 'cursor-pointer hover:shadow-succession-lg hover:translate-y-[-4px]' : ''
      }`}
      onClick={onCardClick}
      role={onCardClick ? 'button' : undefined}
      tabIndex={onCardClick ? 0 : undefined}
      onKeyDown={(event) => {
        if (!onCardClick) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onCardClick();
        }
      }}
    >
      {/* Ribbon is rendered in-flow inside the content column (see below) so it
          never overlaps the bio or the action icons. */}

      {/* Profile Image - 50% on and 50% off the card */}
      {(() => {
        // Build company settings for avatar URL and background color
        const avatarCompanySettings = {
          avatar_directory: currentCompany?.avatar_directory,
          avatar_background_color: currentCompany?.avatar_background_color,
          primary_color: currentCompany?.primary_color,
        };

        // Get avatar style (URL and background color) - automatically detects new vs legacy avatars
        const avatarStyle = getAvatarStyle(user.profile_picture, avatarCompanySettings);

        return (
          <div className="absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 z-10">
            <div
              className="relative w-full h-full rounded-full overflow-hidden border-4 border-succession-bg-primary shadow-succession-lg transition-colors duration-300"
              style={{ backgroundColor: avatarStyle.backgroundColor }}
            >
              {user.profile_picture ? (
                <img
                  src={avatarStyle.url}
                  alt={user.user_name || intl.formatMessage({ id: 'succession.search.list_card.user_alt' })}
                  className="h-full w-full object-cover"
                  style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-succession-primary-10 text-succession-primary text-2xl font-semibold transition-colors duration-300">
                  {getInitials()}
                </div>
              )}
            </div>
            {/* Optional status badge anchored to the bottom of the avatar */}
            {avatarBadge && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 z-20">
                {avatarBadge}
              </div>
            )}
          </div>
        );
      })()}

      {/* Card Content - Add padding on the left for profile image */}
      <div className="flex flex-col p-4 pl-12 w-full relative">
        {/* Header Row */}
        <div className="flex justify-between mb-1 items-start">
          <div className="flex-1 min-w-0 pr-2">
            <h3
              className="font-bold text-xl max-w-full whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer text-succession-primary hover:text-succession-primary-hover transition-colors duration-300"
              onClick={(event) => handleCardClick(event, handleViewProfile)}
            >
              {user.allowed_name || user.user_name}
            </h3>
          </div>
          {/* Action buttons for desktop */}
          <div className="flex space-x-2 items-center">
            {/* View Profile Button */}
            <button
              onClick={(event) => handleCardClick(event, handleViewProfile)}
              className="p-2 rounded-full text-succession-primary hover:bg-succession-primary-10 transition-colors duration-300 succession-focus-visible"
              aria-label={intl.formatMessage({ id: 'succession.search.list_card.view_profile' })}
              title={intl.formatMessage({ id: 'succession.search.list_card.view_profile' })}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </button>
            
            {/* Message Button */}
            <button
              onClick={(event) => handleCardClick(event, handleSendMessage)}
              className={`p-2 rounded-full succession-focus-visible transition-colors duration-300 ${canMessage ? 'text-succession-secondary hover:bg-succession-secondary-10' : 'text-succession-text-muted cursor-not-allowed'}`}
              disabled={!canMessage}
              aria-label={intl.formatMessage({ id: 'succession.search.list_card.send_message' })}
              title={canMessage ? intl.formatMessage({ id: 'succession.search.list_card.send_message' }) : intl.formatMessage({ id: 'succession.search.list_card.messaging_not_available_title' })}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </button>
            
            {/* Toggle Rolodex Button */}
            <button
              onClick={(event) => handleCardClick(event, toggleRolodex)}
              className={`p-2 rounded-full transition-colors duration-300 succession-focus-visible ${
                isUserInRolodex 
                  ? 'text-succession-error bg-succession-error-bg' 
                  : 'text-succession-text-muted hover:text-succession-error hover:bg-succession-error-bg'
              }`}
              disabled={addingToRolodex}
              aria-label={isUserInRolodex ? intl.formatMessage({ id: 'succession.search.list_card.remove_from_rolodex' }) : intl.formatMessage({ id: 'succession.search.list_card.add_to_rolodex' })}
              title={isUserInRolodex ? intl.formatMessage({ id: 'succession.search.list_card.remove_from_rolodex' }) : intl.formatMessage({ id: 'succession.search.list_card.add_to_rolodex' })}
            >
              {isUserInRolodex ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              )}
              
              {addingToRolodex && (
                <span className="absolute top-0 right-0 h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-succession-error opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-succession-error"></span>
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Transition Goals */}
        {Array.isArray(user.transition_goals) && user.transition_goals.filter(Boolean).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {user.transition_goals.filter(Boolean).map((g, idx) => (
              <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-md bg-succession-primary-10 text-xs font-medium text-succession-primary">
                {translateGoal(g)}
              </span>
            ))}
          </div>
        )}

        {/* Location + status ribbon — the ribbon sits across from the location,
            right-aligned, so it never overlaps the bio below. Renders the row
            when there's a location and/or a ribbon to show. */}
        {(user.city || showRibbon) && (
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center text-sm text-succession-text-secondary min-w-0 transition-colors duration-300">
              {user.city && (
                <>
                  <svg className="w-4 h-4 mr-1 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate">{user.city}{user.province ? `, ${user.province}` : ''}</span>
                </>
              )}
            </div>
            {showRibbon && (
              <span className={`shrink-0 self-start inline-flex items-center ${getRibbonColor()} text-white py-0.5 px-2 rounded font-bold text-xs uppercase tracking-wider transition-colors duration-300`}>
                {ribbonText}
              </span>
            )}
          </div>
        )}
        
        {/* Headline */}
        <p className="text-sm text-succession-text-secondary mb-3 line-clamp-3 min-h-[40px] leading-tight pl-1 transition-colors duration-300">
          {user.headline || intl.formatMessage({ id: 'succession.search.list_card.no_headline' })}
        </p>
        
        {/* Details Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 mb-3 bg-succession-bg-secondary p-3 rounded-md border border-succession-border transition-colors duration-300">
          {/* Revenue - Use gross_production for wealth_rj, fallback to annual_revenue */}
          {(isValidNumber(user.gross_production) || isValidNumber(user.annual_revenue)) && (
            <div className="flex items-center gap-1.5">
              <span className="text-succession-primary transition-colors duration-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <div>
                <p className="text-xs font-medium text-succession-text-muted transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.list_card.revenue' })}</p>
                <p className="text-sm font-medium text-succession-text-primary transition-colors duration-300">{formatCurrency(user.gross_production || user.annual_revenue)}</p>
              </div>
            </div>
          )}
          
          {/* AUM */}
          {isValidNumber(user.assets_under_management) && (
            <div className="flex items-center gap-1.5">
              <span className="text-succession-secondary transition-colors duration-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              </span>
              <div>
                <p className="text-xs font-medium text-succession-text-muted transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.list_card.aum' })}</p>
                <p className="text-sm font-medium text-succession-text-primary transition-colors duration-300">{formatCurrency(user.assets_under_management)}</p>
              </div>
            </div>
          )}
          
          {/* Clients */}
          {isValidNumber(user.number_of_clients) && (
            <div className="flex items-center gap-1.5">
              <span className="text-succession-accent transition-colors duration-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </span>
              <div>
                <p className="text-xs font-medium text-succession-text-muted transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.list_card.clients' })}</p>
                <p className="text-sm font-medium text-succession-text-primary transition-colors duration-300">{user.number_of_clients}</p>
              </div>
            </div>
          )}
          
          {/* Years */}
          {isValidNumber(user.years_practicing) && (
            <div className="flex items-center gap-1.5">
              <span className="text-succession-warning transition-colors duration-300">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </span>
              <div>
                <p className="text-xs font-medium text-succession-text-muted transition-colors duration-300">{intl.formatMessage({ id: 'succession.search.list_card.years' })}</p>
                <p className="text-sm font-medium text-succession-text-primary transition-colors duration-300">{user.years_practicing}</p>
              </div>
            </div>
          )}
        </div>

        {/* Optional footer slot — rendered inside the card with a separator */}
        {footer && (
          <div className="border-t border-succession-border mt-3 pt-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

ListCard.propTypes = {
  user: PropTypes.object.isRequired,
  currentCompany: PropTypes.object,
  enqueueSnackbar: PropTypes.func,
  onCardClick: PropTypes.func,
  avatarBadge: PropTypes.node,
  footer: PropTypes.node,
};

export default withSnackbar(ListCard); 